import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import MuscleMap from "@/components/MuscleMap";
import {
  ClipboardList, Plus, Trash2, Pencil, Loader2, X, Dumbbell, AlertCircle, GripVertical,
} from "lucide-react";

const MUSCLE_GROUPS = ["Peito", "Costas", "Pernas", "Ombro", "Bíceps", "Tríceps", "Antebraço", "Glúteo", "Panturrilha", "Core/Abdômen"];

export default function WorkoutTemplates() {
  const [templates, setTemplates] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list"); // list | editor
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [items, setItems] = useState([]);
  const [pickGroup, setPickGroup] = useState("todos");
  const [pickId, setPickId] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, e] = await Promise.all([api.get("/templates"), api.get("/exercises")]);
      setTemplates(t.data);
      setExercises(e.data);
    } catch (_) {
      toast.error("Falha ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditId(null); setName(""); setItems([]); setErrors({}); setMode("editor"); };
  const startEdit = (t) => {
    setEditId(t.id); setName(t.name);
    setItems((t.items || []).map((i) => ({ ...i, key: Math.random().toString(36).slice(2) })));
    setErrors({}); setMode("editor");
  };

  const filteredExercises = pickGroup === "todos" ? exercises : exercises.filter((e) => e.muscle_group === pickGroup);

  const addItem = () => {
    if (!pickId) return;
    const ex = exercises.find((e) => e.id === pickId);
    if (!ex) return;
    setItems((prev) => [...prev, {
      key: Math.random().toString(36).slice(2),
      exercise_id: ex.id, exercise_name: ex.name, muscle_group: ex.muscle_group,
      sets: "", reps: "", load_note: "",
    }]);
    setPickId("");
    setErrors((e) => ({ ...e, items: undefined }));
  };

  const updateItem = (key, field, value) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, [field]: value } : i)));
  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const save = async () => {
    const err = {};
    if (!name.trim()) err.name = "Nome do modelo é obrigatório";
    if (items.length === 0) err.items = "Adicione ao menos um exercício";
    setErrors(err);
    if (Object.keys(err).length) { toast.error("Preencha os campos destacados."); return; }
    setSaving(true);
    try {
      const payload = {
        name,
        items: items.map(({ key, ...rest }) => rest),
      };
      if (editId) await api.put(`/templates/${editId}`, payload);
      else await api.post("/templates", payload);
      toast.success("Modelo salvo com sucesso!");
      setMode("list");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Excluir o modelo "${t.name}"?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Modelo excluído");
      load();
    } catch (_) {
      toast.error("Falha ao excluir");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-emerald-500 animate-spin" /></div>;

  if (mode === "editor") {
    return (
      <div className="space-y-6 max-w-3xl pb-24">
        <div className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5 sm:p-7 space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-300">Nome do modelo *</Label>
            <Input data-testid="template-name-input" value={name} onChange={(e) => { setName(e.target.value); setErrors((x) => ({ ...x, name: undefined })); }}
              placeholder='Ex: Treino A — Iniciante'
              className={`h-12 bg-[#0F1520] text-slate-100 rounded-xl focus-visible:ring-emerald-500 ${errors.name ? "border-red-500" : "border-[#263147]"}`} />
            {errors.name && <p className="text-xs text-red-400 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.name}</p>}
          </div>

          <div className="border-t border-[#263147] pt-5">
            <Label className="text-slate-300 mb-3 block">Adicionar exercício da biblioteca</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={pickGroup} onValueChange={setPickGroup}>
                <SelectTrigger data-testid="template-pick-group" className="h-12 sm:w-40 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                  <SelectItem value="todos">Todos</SelectItem>
                  {MUSCLE_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={pickId} onValueChange={setPickId}>
                <SelectTrigger data-testid="template-pick-exercise" className="h-12 flex-1 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl"><SelectValue placeholder="Selecione o exercício" /></SelectTrigger>
                <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100 max-h-72">
                  {filteredExercises.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.equipment})</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" data-testid="template-add-exercise" onClick={addItem} disabled={!pickId}
                className="h-12 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl px-5">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {errors.items && <p className="text-xs text-red-400 mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.items}</p>}
          </div>

          <div className="space-y-3" data-testid="template-items">
            {items.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Nenhum exercício adicionado ainda.</p>
            ) : items.map((it, idx) => (
              <div key={it.key} className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <GripVertical className="w-4 h-4 text-slate-600" />
                  <span className="w-6 h-6 rounded-md bg-emerald-500/15 text-emerald-400 text-xs font-semibold flex items-center justify-center">{idx + 1}</span>
                  <span className="font-medium text-slate-100 flex-1">{it.exercise_name}</span>
                  <Badge className="bg-[#181F2C] text-slate-400 border-[#263147]">{it.muscle_group}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => removeItem(it.key)} className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10 rounded-lg"><X className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-slate-500">Séries</Label>
                    <Input value={it.sets} onChange={(e) => updateItem(it.key, "sets", e.target.value)} placeholder="3" className="h-10 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Repetições</Label>
                    <Input value={it.reps} onChange={(e) => updateItem(it.key, "reps", e.target.value)} placeholder="12" className="h-10 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Carga / Obs.</Label>
                    <Input value={it.load_note} onChange={(e) => updateItem(it.key, "load_note", e.target.value)} placeholder="20kg" className="h-10 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg mt-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5 sm:p-7">
          <h3 className="text-sm font-semibold text-slate-300 mb-4 text-center">Grupos musculares trabalhados</h3>
          <MuscleMap groups={items.map((i) => i.muscle_group)} showLegend />
        </div>

        <div className="flex gap-3">
          <Button data-testid="save-template-button" onClick={save} disabled={saving} className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold text-base rounded-xl shadow-lg shadow-emerald-500/20">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salvar Modelo"}
          </Button>
          <Button variant="outline" onClick={() => setMode("list")} className="h-14 px-6 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button data-testid="new-template-button" onClick={startNew} className="h-12 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl px-5">
          <Plus className="w-4 h-4 mr-2" /> Novo Modelo
        </Button>
      </div>
      {templates.length === 0 ? (
        <div className="text-center py-20 text-slate-500">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Nenhum modelo de treino criado ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="template-list">
          {templates.map((t) => (
            <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-[#181F2C] border border-[#263147] rounded-2xl p-5 hover:border-[#34435f] transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center"><Dumbbell className="w-5 h-5 text-emerald-400" /></span>
                  <div>
                    <h3 className="font-semibold text-slate-100">{t.name}</h3>
                    <p className="text-xs text-slate-500">{t.items?.length || 0} exercícios</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => startEdit(t)} className="h-8 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-lg"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" data-testid={`delete-template-${t.id}`} onClick={() => remove(t)} className="h-8 border-[#263147] bg-[#0F1520] text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <div className="mt-4 space-y-1.5">
                {t.items?.slice(0, 5).map((i, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                    <span className="text-slate-600 text-xs w-4">{idx + 1}.</span>
                    <span className="flex-1 truncate">{i.exercise_name}</span>
                    <span className="text-xs text-slate-500">{[i.sets && `${i.sets}x`, i.reps].filter(Boolean).join(" ")}</span>
                  </div>
                ))}
                {t.items?.length > 5 && <p className="text-xs text-slate-600">+{t.items.length - 5} mais...</p>}
              </div>
              <div className="mt-4 pt-4 border-t border-[#263147]">
                <MuscleMap groups={(t.items || []).map((i) => i.muscle_group)} showLegend showTitle />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
